import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { apiService } from "../services/api";

interface Client {
    id: number;
    name: string;
    code: string;
    isEnabled: boolean;
    contactInfo?: string;
    comment?: string;
    supplierId?: number;
}

const ClientsManagement: React.FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState<number | null>(null);

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiService.getClients(true); // all=true для админа
            // API возвращает { success: true, data: [...], total: ... }
            const clientsData = Array.isArray(response.data) 
                ? response.data 
                : (response.data?.data || []);
            setClients(clientsData);
        } catch (err: any) {
            setError(err.message || "Ошибка загрузки клиентов");
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleClientStatus = async (clientId: number, currentStatus: boolean) => {
        setUpdating(clientId);
        try {
            // Вызываем API для обновления статуса
            await apiService.updateClient(clientId, { isEnabled: !currentStatus });

            // Обновляем локальное состояние
            setClients((prev) =>
                prev.map((client) => (client.id === clientId ? { ...client, isEnabled: !currentStatus } : client)),
            );
        } catch (err: any) {
            setError(err.message || "Ошибка обновления статуса");
        } finally {
            setUpdating(null);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Управление клиентами
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Видимость в фильтре:</strong> Отключенные клиенты (isEnabled = 0) не отображаются в фильтре
                    &quot;Клиент&quot; на странице статистики.
                </Typography>
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Название</TableCell>
                                    <TableCell>Код</TableCell>
                                    <TableCell>Контакты</TableCell>
                                    <TableCell>Комментарий</TableCell>
                                    <TableCell align="center">Статус</TableCell>
                                    <TableCell align="center">Видимость в фильтре</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            Нет клиентов
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    clients.map((client) => (
                                        <TableRow key={client.id}>
                                            <TableCell>{client.id}</TableCell>
                                            <TableCell>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {client.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={client.code} size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {client.contactInfo || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {client.comment || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {client.isEnabled ? (
                                                    <Chip
                                                        icon={<CheckCircleIcon />}
                                                        label="Активен"
                                                        color="success"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<CancelIcon />}
                                                        label="Отключен"
                                                        color="error"
                                                        size="small"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Switch
                                                    checked={client.isEnabled}
                                                    onChange={() => toggleClientStatus(client.id, client.isEnabled)}
                                                    disabled={updating === client.id}
                                                    color="primary"
                                                />
                                                {updating === client.id && (
                                                    <CircularProgress size={20} sx={{ ml: 1 }} />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ClientsManagement;
